import React from 'react'
import { Box, makeStyles, Theme } from '@material-ui/core'

import { FormSaveAction } from './FormSaveButton'
import { SetupArtifactChooser } from './SetupArtifactChooser'
import {
    SelectedBarSummarySection,
    SetupArtifactSelectedBar,
} from './SetupArtifactSelectedBar'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    SelectedSetupConfig,
    SetupArtifactKind,
    SetupConfigOption,
} from './setupArtifacts'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
    },
    selectedWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
        minHeight: 0,
        padding: theme.spacing(0.5, 1, 0),
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    selectedColumn: {
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
    },
    editor: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
}))

/**
 * Shared Setup tab shell: chooser when nothing selected, else selected bar + editor.
 */
export const SetupArtifactShell: React.FC<{
    kind: SetupArtifactKind
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    discourageScratch?: boolean
    readinessLabel?: string
    summarySections?: SelectedBarSummarySection[]
    /** Active-season / org lock — hide Change and show chooser as disabled. */
    contentLocked?: boolean
    saveAction?: FormSaveAction
    children: React.ReactNode
}> = ({
    kind,
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    discourageScratch,
    readinessLabel,
    summarySections,
    contentLocked = false,
    saveAction,
    children,
}) => {
    const classes = useStyles()

    if (!selected) {
        if (contentLocked) {
            return (
                <Box className={classes.selectedWrap}>
                    <Box className={classes.selectedColumn}>
                        <Box className={classes.editor}>{children}</Box>
                    </Box>
                </Box>
            )
        }
        return (
            <SetupArtifactChooser
                kind={kind}
                existing={existing}
                onExistingChange={onExistingChange}
                onSelected={onSelected}
                discourageScratch={discourageScratch}
            />
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.selectedWrap}>
                <Box className={classes.selectedColumn}>
                    <SetupArtifactSelectedBar
                        kind={kind}
                        selected={selected}
                        onChange={
                            selected.orgMandated || contentLocked
                                ? undefined
                                : onClearSelection
                        }
                        readinessLabel={readinessLabel}
                        summarySections={summarySections}
                        saveAction={saveAction}
                    />
                    <Box className={classes.editor}>{children}</Box>
                </Box>
            </Box>
        </Box>
    )
}
